/**
 * Fake transport that answers the endpoints in KnoWledgeX/06-api-spec.md with
 * the same envelope: { code, message, data }. Adds latency, optional random
 * failures, pagination meta, filters and localStorage persistence for anything
 * the learner mutates (progress, enrollments, orders, bookings, admin edits).
 *
 * When the Go API exists: set VITE_USE_MOCK=false and `request()` in api.js
 * hits /api/v1 instead. Component code does not change.
 */
import * as seed from './data'

const LS_KEY = 'kx.mock.state.v1'
const env = import.meta.env
const LAT_MIN = +(env.VITE_MOCK_LATENCY_MIN ?? 220)
const LAT_MAX = +(env.VITE_MOCK_LATENCY_MAX ?? 700)

/* --------------------------------------------------------------- state --- */
const fresh = () => ({
  providers: clone(seed.providers),
  categories: clone(seed.categories),
  instructors: clone(seed.instructors),
  courses: clone(seed.courses),
  modules: clone(seed.modules),
  lessons: clone(seed.lessons),
  quizzes: clone(seed.quizzes),
  liveClasses: clone(seed.liveClasses),
  qaQuestions: clone(seed.qaQuestions),
  consultationSlots: clone(seed.consultationSlots),
  enrollments: clone(seed.enrollments),
  lessonProgress: clone(seed.lessonProgress),
  orders: clone(seed.orders),
  coupons: clone(seed.coupons),
  priceTiers: clone(seed.priceTiers),
  reviews: clone(seed.reviews),
  certificates: clone(seed.certificates),
  bookings: clone(seed.bookings),
  payouts: clone(seed.payouts),
  notifications: clone(seed.notifications),
  quizAttempts: [],
  // Catatan murid. Didaftarkan DI SINI, bukan diberi nilai sambil jalan, dengan
  // alasan yang sama seperti currentUserId di bawah: kalau tidak, resetMockDb()
  // meninggalkan catatan lama dan bentuk db yang hidup berbeda dari fresh().
  lessonNotes: [],
  // Session + credential state. Listed here so resetMockDb() actually signs you
  // out and drops changed passwords; when these were only ever assigned ad hoc,
  // fresh() and the live db had different shapes.
  currentUserId: null,
  passwords: {},
})

const clone = (v) => JSON.parse(JSON.stringify(v))

let db = load()

function load() {
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (raw) return { ...fresh(), ...JSON.parse(raw) }
  } catch { /* ignore */ }
  return fresh()
}
function persist() {
  try { localStorage.setItem(LS_KEY, JSON.stringify(db)) } catch { /* quota */ }
}
export function resetMockDb() { db = fresh(); persist() }

/**
 * The signed-in user, falling back to the demo student exactly like the Go API
 * does. Before this existed the mock kept no session whatsoever, so every
 * /me/* route silently answered as seed.users[0].
 */
const me = () => seed.users.find((u) => u.id === db.currentUserId) || seed.users[0]

/** Shared demo password for every mock account (CLAUDE.md §4). */

/**
 * Resolve a course for a PUBLIC route, hiding anything not yet published.
 *
 * The catalog list filters on status; the detail routes used not to, so a draft
 * stayed readable to anyone holding its slug or id. Mirrors visibleCourse() in
 * the Go API — only a logged-in admin (or the expert who teaches it) may see a
 * course before it is published.
 */
/**
 * Urutan kanonik kelas — cermin orderCourses() di internal/api/order.go.
 *
 * Tanpa ini mock akan menyajikan urutan penyisipan array, yang kebetulan stabil
 * dan kebetulan rapi. Postgres tidak menjanjikan keduanya, jadi mock yang tidak
 * mengurutkan akan membuat bug urutan MUSTAHIL terlihat saat VITE_USE_MOCK=true
 * — yaitu mode yang dipakai build statis yang ter-deploy.
 *
 * position 0 = belum bernomor, diletakkan di belakang. Sama dengan sisi Go.
 */
const byCanonicalOrder = (a, b) => {
  const pa = a.position || 0, pb = b.position || 0
  if ((pa === 0) !== (pb === 0)) return pa === 0 ? 1 : -1
  if (pa !== pb) return pa - pb
  return String(b.created_at || '').localeCompare(String(a.created_at || ''))
}
const ordered = (list) => [...list].sort(byCanonicalOrder)

const visibleCourse = (idOrSlug) => {
  const c = db.courses.find((x) => x.slug === idOrSlug || x.id === idOrSlug)
  if (!c) fail(404, 'Kelas tidak ditemukan')
  if (c.status === 'published') return c
  const u = db.currentUserId ? me() : null
  const own = u?.role === 'instructor' && db.instructors.some((i) => i.user_id === u.id && i.id === c.instructor_id)
  const admin = u?.role === 'platform_admin' || (u?.role === 'provider_admin' && u.provider_id === c.provider_id)
  if (!admin && !own) fail(404, 'Kelas tidak ditemukan')
  return c
}

/** prereqInfo — cermin a.prereqInfo() di Go. null = tanpa prasyarat, dan
 *  klien merender bloknya hanya kalau kunci ini terisi.
 *  "Lulus" = enrollment berstatus completed, definisi yang sama dengan server. */
const prereqInfo = (c) => {
  if (!c?.prereq_course_id) return null
  const p = db.courses.find((x) => x.id === c.prereq_course_id)
  if (!p) return null
  return {
    id: p.id, slug: p.slug, title: p.title,
    required: !!c.prereq_required,
    met: db.enrollments.some((e) => e.course_id === p.id && e.status === 'completed'),
  }
}

/* ------------------------------------------------------------- helpers --- */
const sleep = (ms) => new Promise((r) => setTimeout(r, ms))
const latency = () => LAT_MIN + Math.random() * (LAT_MAX - LAT_MIN)
const ok = (data, message = 'OK') => ({ code: 200, message, data })
const uidNew = (p) => `${p}-${Math.random().toString(36).slice(2, 8)}`

export class ApiError extends Error {
  constructor(code, message, data = null) { super(message); this.code = code; this.data = data }
}
const fail = (code, message, data) => { throw new ApiError(code, message, data) }

/* ---- gerbang top-up (cermin defaultTopupGateways di Go) -------------------
 * Angka-angkanya harus sama dengan `defaultTopupGateways()` di
 * `internal/api/settings.go`. Kalau berbeda, harga yang ditinjau saat mendesain
 * bukan harga yang ditagih — dan bedanya baru ketahuan dari tagihan sungguhan.
 *
 * `money_digits` 0 untuk IDR: rupiah tidak punya sub-unit, jadi 16000 berarti
 * Rp 16.000. Membaginya 100 membuat setiap tagihan tampil seperseratus nilainya.
 * -------------------------------------------------------------------------- */
/**
 * `key` membedakan BARIS harga, dan sejak satu gerbang boleh punya lebih dari
 * satu mata uang ia bukan lagi sama dengan `gateway`. Mock harus memakai kunci
 * yang sama dengan Go — kalau di sini pemilihan tetap lewat nama gerbang,
 * memilih USD akan "berhasil" di mock dan menagih MYR di API sungguhan.
 */
const MOCK_GATEWAYS = [
  // Angka WAJIB sama persis dengan defaultTopupGateways() di
  // internal/api/settings.go — termasuk satuannya: fee_bps adalah BASIS POIN
  // (400 = 4,00%) dan rate_minor sub-unit mata uang (IDR tanpa desimal).
  // Alasan tiap angka, beserta sumber tarifnya, ada di berkas Go itu.
  //
  // Kalau mock memakai tarif yang lebih murah, harga yang ditinjau saat
  // mendesain bukan harga yang benar-benar ditagih — dan selisihnya baru
  // ketahuan dari pembeli pertama.
  { key: 'stripe_myr', gateway: 'stripe', currency: 'MYR', rate_minor: 450, fee_bps: 400, fee_flat_minor: 100, min_credits: 500, money_digits: 2, automatic: true },
  { key: 'stripe_usd', gateway: 'stripe', currency: 'USD', rate_minor: 115, fee_bps: 600, fee_flat_minor: 25, min_credits: 500, money_digits: 2, automatic: true },
  { key: 'doku_idr', gateway: 'doku', currency: 'IDR', rate_minor: 20000, fee_bps: 311, fee_flat_minor: 2220, min_credits: 500, money_digits: 0, automatic: true },
  { key: 'manual_myr', gateway: 'manual', currency: 'MYR', rate_minor: 450, fee_bps: 0, fee_flat_minor: 0, min_credits: 1000, money_digits: 2, automatic: false },
]

/**
 * Cari baris harga: kunci dulu, lalu nama gerbang telanjang.
 *
 * Urutan ini sama persis dengan pricingFor() di Go, dan itu yang menjaga
 * "stripe" tetap berarti baris MYR — bukan baris USD yang kebetulan ditambahkan
 * belakangan.
 */
function mockGateway(want) {
  const w = String(want || '').toLowerCase().trim()
  if (!w) return null
  return MOCK_GATEWAYS.find((g) => g.key === w)
    || MOCK_GATEWAYS.find((g) => g.gateway.toLowerCase() === w)
    || null
}

// Tarif cara bayar, gross-up, dan penawaran top-up TIDAK ditiru di sini lagi.
//
// Semuanya pindah ke The Muslim bersama dompet. Mock yang tetap menyimpan
// salinan tarif akan menyimpang dari yang sungguhan tanpa ada yang menyadari —
// dan menyimpang di angka uang adalah bentuk paling mahal dari "mirip".

function paginate(items, q = {}) {
  const page = Math.max(1, +(q.page || 1))
  const per = Math.max(1, +(q.per_page || 12))
  const total = items.length
  return {
    items: items.slice((page - 1) * per, page * per),
    meta: { page, per_page: per, total, total_pages: Math.max(1, Math.ceil(total / per)) },
  }
}

const catOf = (id) => db.categories.find((c) => c.id === id)
const insOf = (id) => db.instructors.find((i) => i.id === id)
const prvOf = (id) => db.providers.find((p) => p.id === id)

/* ------------------------------------------- akun login pakar (mock) --- */

/**
 * Ringkasan akun untuk panel admin, sepadan dengan a.accountInfo() di Go.
 *
 * Mock tidak punya tabel users yang bisa ditulis, jadi emailnya disimpan
 * langsung di baris pakar. Yang harus SAMA dengan sisi Go cuma bentuk
 * payload-nya — itulah yang dibaca komponen.
 *
 * `linked` = sudah pernah benar-benar masuk lewat The Muslim. Selama masih
 * false, akunnya cuma tempat menunggu, dan beda itu perlu terlihat oleh admin
 * yang sedang bertanya "kenapa dia belum bisa masuk?".
 */
const accountOf = (i) => {
  const email = i.account_email || db.users?.find?.((u) => u.id === i.user_id)?.email || ''
  if (!email) return null
  return { id: i.user_id || null, email, role: 'instructor', linked: !!i.user_id, last_login_at: null }
}

/**
 * Menyambungkan pakar ke akun ber-email tertentu.
 *
 * Kosong = JANGAN UBAH, bukan "putuskan": form ini dibuka untuk mengganti bio
 * atau foto jauh lebih sering daripada untuk mengurus akun.
 *
 * Penjaga yang ditiru dari Go: alamat yang sudah dipakai profil pakar LAIN
 * ditolak. Satu huruf salah ketik cukup membuat dua orang berbagi satu akun,
 * dan yang belakangan masuk akan mengedit kelas serta menerima pendapatan
 * orang lain — tanpa apa pun di layar yang menyatakannya.
 */
function linkAccountMock(ins, raw) {
  const email = String(raw || '').trim().toLowerCase()
  if (!email) return
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) fail(400, 'Format email tidak benar.')
  const other = db.instructors.find((x) => x.id !== ins.id && (x.account_email || '').toLowerCase() === email)
  if (other) fail(409, `Email ini sudah dipakai profil pakar lain (${other.name}).`)
  ins.account_email = email
}

/**
 * The signed-in user's own expert profile, shaped like GET /instructors/{slug}
 * plus the completeness checklist that drives the "get verified" prompt.
 * 404s when the account has no linked profile, same as the Go handler.
 */
function myInstructor() {
  const u = me()
  const i = db.instructors.find((x) => x.user_id === u.id)
  if (!i) fail(404, 'Profil pakar tidak ditemukan untuk akun ini.')
  const bio = (i.bio || '').trim()
  return {
    ...i,
    provider: prvOf(i.provider_id),
    category: catOf(i.category_id),
    courses: db.courses.filter((c) => c.instructor_id === i.id),
    slots: db.consultationSlots.filter((s) => s.instructor_id === i.id && !s.is_booked),
    answered_count: db.qaQuestions.filter((qq) => qq.instructor_id === i.id && qq.status === 'answered').length,
    completeness: {
      avatar: !!i.avatar_url,
      headline: !!(i.headline || '').trim(),
      bio: bio.length >= 120,
      credentials: (i.credentials || []).length > 0,
      social: Object.keys(i.social_links || {}).length > 0,
    },
  }
}

/** Course shaped for cards/detail: joins in the names the UI needs. */
/** Bahasa yang teks katalognya BENAR-BENAR ada. Cerminan models.TextLangs di Go.
 *
 *  Dihitung, tidak pernah dibaca dari kolom yang diketik — sebuah kolom "bahasa
 *  yang didukung" yang diisi tangan adalah janji tanpa apa pun yang menopangnya,
 *  dan pembeli memutuskan berdasarkan janji itu.
 *
 *  Mock ikut menghitungnya supaya "kelas ini tersedia dalam 3 bahasa" tidak
 *  hanya benar di server: build statis yang ter-deploy berjalan di atas berkas
 *  ini, jadi kalau di sini dikarang, di situlah desainnya ditinjau. */
const LANG_ORDER = ['id', 'en', 'ms', 'ar']
const isiAda = (v) => (Array.isArray(v) ? v.length > 0 : String(v ?? '').trim() !== '')
export function textLangsOf(c) {
  const src = c?.source_lang || 'id'
  const tr = c?.translations || {}
  const out = []
  if (isiAda(c?.title) && isiAda(c?.description)) out.push(src)
  LANG_ORDER.forEach((l) => {
    if (l === src) return
    const f = tr[l]?.fields || {}
    if (isiAda(f.title) && isiAda(f.description)) out.push(l)
  })
  return out
}

export function decorate(c) {
  const cat = catOf(c.category_id) || {}
  const ins = insOf(c.instructor_id) || {}
  const prv = prvOf(c.provider_id) || {}
  const ls = db.lessons.filter((l) => l.course_id === c.id)
  const enr = db.enrollments.find((e) => e.course_id === c.id)
  return {
    ...c,
    category: { id: cat.id, key: cat.key, name: cat.name_id, name_en: cat.name_en, name_ms: cat.name_ms, name_ar: cat.name_ar, accent_color: cat.accent_color, icon: cat.icon },
    instructor: { id: ins.id, name: ins.name, slug: ins.slug, avatar_url: ins.avatar_url, is_verified: ins.is_verified, headline: ins.headline },
    provider: { id: prv.id, name: prv.name, slug: prv.slug, is_verified: prv.is_verified, logo_url: prv.logo_url },
    lesson_count: ls.length,
    module_count: db.modules.filter((m) => m.course_id === c.id).length,
    has_quiz: ls.some((l) => l.type === 'quiz'),
    has_live: ls.some((l) => l.type === 'live'),
    text_langs: textLangsOf(c),
    enrollment: enr ? { percent_complete: enr.percent_complete, status: enr.status, id: enr.id } : null,
  }
}

function curriculumOf(courseId, { unlocked = false } = {}) {
  return db.modules
    .filter((m) => m.course_id === courseId)
    .sort((a, b) => a.position - b.position)
    .map((m) => ({
      ...m,
      lessons: db.lessons
        .filter((l) => l.module_id === m.id)
        .sort((a, b) => a.position - b.position)
        .map((l) => {
          const p = db.lessonProgress.find((x) => x.lesson_id === l.id)
          return {
            ...l,
            locked: !unlocked && !l.is_preview,
            progress: p ? { completed: p.completed, percent_watched: p.percent_watched, last_position_sec: p.last_position_sec } : null,
          }
        }),
    }))
}

function recalcEnrollment(enrollmentId) {
  const e = db.enrollments.find((x) => x.id === enrollmentId)
  if (!e) return null
  const ls = db.lessons.filter((l) => l.course_id === e.course_id)
  const done = db.lessonProgress.filter((p) => p.enrollment_id === e.id && p.completed).length
  e.percent_complete = Math.round((done / ls.length) * 100)
  if (e.percent_complete >= 100 && e.status !== 'completed') {
    e.status = 'completed'
    e.completed_at = new Date().toISOString()
    const c = db.courses.find((x) => x.id === e.course_id)
    if (c.certificate_enabled && !e.certificate_id) {
      const id = uidNew('cer')
      e.certificate_id = id
      db.certificates.push({
        id, serial_number: `KX-2026-${Math.floor(2000 + Math.random() * 8000)}`,
        verification_code: `KX${Math.random().toString(36).slice(2, 10).toUpperCase()}`,
        user_id: e.user_id, user_name: seed.users[0].name, course_id: c.id, course_title: c.title,
        provider_name: prvOf(c.provider_id).name, instructor_name: insOf(c.instructor_id).name,
        enrollment_id: e.id, issued_at: new Date().toISOString(), completion_date: new Date().toISOString(),
        hours: Math.round(c.estimated_minutes / 60), final_score: 90, pdf_url: '#', status: 'issued', is_new: true,
      })
      db.notifications.unshift({ id: uidNew('ntf'), kind: 'certificate_issued', title: 'Sertifikat terbit', body: `Sertifikat ${c.title} sudah bisa diunduh.`, created_at: new Date().toISOString(), read_at: null })
    }
  }
  persist()
  return e
}

// Kolom yang boleh ditulis PUT /admin/courses/{id} — salinan `courseUpdatable`
// di internal/api/admin.go. provider_id, slug, published_at dan semua
// counter/rating sengaja TIDAK ada: itu milik sistem, bukan form editor.
const ADMIN_COURSE_FIELDS = new Set([
  'title', 'subtitle', 'description', 'level', 'language', 'access_type',
  'thumbnail_url', 'promo_video_url', 'category_id', 'instructor_id',
  'store_sku_ios', 'store_sku_android', 'price', 'estimated_minutes',
  'compare_at_price', 'access_days', 'is_free', 'certificate_enabled',
  'objectives', 'requirements', 'target_audience', 'tags', 'subtitle_langs',
  'status', 'prereq_course_id', 'prereq_required',
])
const LIST_FIELDS = new Set(['objectives', 'requirements', 'target_audience', 'tags', 'subtitle_langs'])

/* -------------------------------------------------------------- routing --- */

// Dompet mock DIBUANG.
//
// Saldo dan buku besarnya milik The Muslim sekarang, dan halaman uang di
// KnowledgeX tinggal tautan keluar. Menyisakan dompet palsu di sini berarti
// mock punya angka yang tidak ada padanannya di mana pun — dan itu angka uang.

/** Setelan pemberitahuan email — cermin defaultNotifySettings() di Go. */
const MOCK_NOTIFY = {
  curation_enabled: true,
  curation_emails: ['radhsyn83@gmail.com'],
  decision_enabled: true,
}
const mockNotify = () => ({ ...MOCK_NOTIFY, ...(db.notify || {}) })

/**
 * cleanEmails — cermin fungsi bernama sama di internal/api/settings_notify.go.
 *
 * SELALU mengembalikan array, tidak pernah null: satu `null` di kolom daftar
 * membuat `.map()` di formulir melempar dan seluruh halaman Pengaturan mati,
 * bukan cuma satu blok kosong.
 */
function cleanEmails(list) {
  const seen = new Set()
  const out = []
  for (const raw of Array.isArray(list) ? list : []) {
    const s = String(raw ?? '').trim()
    const at = s.indexOf('@')
    if (at <= 0 || at === s.length - 1 || !s.slice(at + 1).includes('.')) continue
    const k = s.toLowerCase()
    if (seen.has(k)) continue
    seen.add(k)
    out.push(s)
  }
  return out
}

const routes = [
  /* ---- auth ---- */
  // `POST /auth/login` dan `GET /auth/demo-accounts` SUDAH DIHAPUS di sini,
  // sama seperti di server. Mock memang bukan batas keamanan — ia alat desain —
  // tapi membiarkan pintu kedua hidup di sini punya akibat yang nyata: build
  // statis yang ter-deploy berjalan di atas mock (CLAUDE.md §6), jadi
  // "KnowledgeX cuma punya satu pintu" akan tetap tidak benar di satu-satunya
  // tempat yang bisa dibuka orang luar.
  ['POST', /^\/auth\/logout$/, () => { db.currentUserId = null; persist(); return ok({ ok: true }) }],
  ['GET', /^\/auth\/me$/, () => {
    const u = me()
    return ok({ ...u, enrolled_count: db.enrollments.length, certificate_count: db.certificates.length })
  }],


  /* ---- SSO The Muslim ----
     Mock TIDAK bisa benar-benar ber-SSO: tidak ada server otorisasi di
     peramban. Yang wajib ditiru hanyalah BENTUK balasannya, karena tanpa rute
     ini `mockRequest` melempar "404 Mock route belum ada" — dan build statis
     yang ter-deploy berjalan di atas mock (CLAUDE.md §6), jadi tombol Masuk
     akan berhenti dengan galat merah alih-alih memberi tahu apa yang terjadi.

     `authorize_url` menunjuk balik ke /auth/callback sendiri sambil membawa
     kode palsu, jadi alur dua langkahnya tetap bisa dijalani utuh saat
     mendesain — hanya tanpa The Muslim di tengahnya. */
  /* `account_url` sengaja diisi alamat SUNGGUHAN, bukan dikosongkan.

     Kosong berarti tombol "Akun The Muslim" tidak pernah dirender di mock —
     dan mock adalah yang berjalan di build statis yang ter-deploy, jadi
     tombolnya akan absen persis di tempat desainnya ditinjau. Alamat ini
     publik dan tidak membawa apa-apa, jadi menautkannya tidak berbahaya;
     yang berbahaya adalah menu yang terlihat lengkap di mock lalu berbeda
     di produksi. */
  ['GET', /^\/auth\/config$/, () => ok({
    account_url: 'https://the-muslim.app/accounts/profile',
    // Alamat halaman uang ikut DIISI, bukan dikosongkan.
    //
    // Kosong berarti tautan dompet/afiliator/pencairan tidak pernah dirender
    // saat VITE_USE_MOCK=true — dan mock itu yang berjalan di build statis
    // yang ter-deploy, jadi tautannya absen persis di tempat desainnya
    // ditinjau.
    credits_url: 'https://the-muslim.app/accounts/credits',
    affiliate_url: 'https://the-muslim.app/accounts/affiliate',
    payouts_url: 'https://the-muslim.app/accounts/payouts',
    configured: true,
  })],
  /* Saldo. Mock TIDAK punya dompet — dompetnya milik bank — jadi angkanya
     karangan, dan itu benar untuk alat desain.

     Yang penting bentuknya: `available` ikut dikirim, karena itulah yang
     dipakai klien memutuskan merender atau tidak. Mock yang mengirim saldo
     telanjang tanpa saklar itu membuat cabang "tanpa dompet" — akun belum
     tertaut, bank tidak terjangkau — tidak pernah terlihat saat mendesain,
     lalu ia muncul pertama kali di produksi. `scale` juga wajib: klien yang
     memakai 100 miliknya sendiri akan merender saldo seratus kali salah pada
     hari skalanya berubah. */
  ['GET', /^\/me\/credits$/, () => ok({
    available: true,
    balance: 700,
    withdrawable: 0,
    scale: 100,
    credits_url: 'https://the-muslim.app/accounts/credits',
  })],
  ['GET', /^\/auth\/login-url$/, ({ q }) => {
    const state = `mock-state-${Date.now()}`
    db.ssoStates = { ...(db.ssoStates || {}), [state]: q.redirect || '/' }
    persist()
    return ok({ authorize_url: `/auth/callback?code=mock-code&state=${state}`, state })
  }],
  ['POST', /^\/auth\/callback$/, ({ body }) => {
    const redirect = (db.ssoStates || {})[body.state]
    if (redirect === undefined) fail(401, 'Sesi login sudah kedaluwarsa. Silakan masuk lagi.')
    // Sekali pakai, sama seperti di server. Kalau mock membiarkan state dipakai
    // ulang, bug "callback ter-render dua kali" cuma muncul di produksi.
    delete db.ssoStates[body.state]
    const u = me()
    db.currentUserId = u.id
    persist()
    return ok({
      access_token: `mock.${u.id}.${Date.now()}`, refresh_token: 'mock.refresh',
      expires_in: 3600, user: u, redirect_to: redirect,
      id_token: 'mock.id-token',
    })
  }],
  /* Keluar. `logout_url` sengaja KOSONG di mock: tidak ada sesi The Muslim
     yang bisa diakhiri dari peramban, dan mengarang URL ke sana akan
     memindahkan orang keluar dari maket di tengah alur desain. Klien sudah
     menanganinya — url kosong berarti "cukup bersihkan sesi di sini". */
  ['POST', /^\/auth\/logout$/, () => {
    db.currentUserId = null
    persist()
    return ok({ ok: true, logout_url: '' })
  }],

  /* ---- catalog ---- */
  ['GET', /^\/categories$/, () => ok(db.categories.filter((c) => c.is_active))],
  ['GET', /^\/providers$/, ({ q }) => ok(paginate(db.providers.filter((p) => (q.active ? p.is_active : true)), { per_page: 20, ...q }))],
  ['GET', /^\/providers\/([^/]+)$/, ({ m }) => {
    const p = db.providers.find((x) => x.slug === m[1] || x.id === m[1])
    if (!p) fail(404, 'Provider tidak ditemukan')
    return ok({
      ...p,
      courses: ordered(db.courses.filter((c) => c.provider_id === p.id && c.status === 'published')).map(decorate),
      instructors: db.instructors.filter((i) => i.provider_id === p.id),
    })
  }],
  ['GET', /^\/instructors$/, ({ q }) => {
    let list = db.instructors.filter((i) => i.is_active !== false)
    // Verified first, then busiest, then a stable id tiebreak — mirrors the Go
    // handler so page boundaries don't drift between requests.
    list.sort((a, b) => (b.is_verified - a.is_verified) || (b.student_count - a.student_count) || a.id.localeCompare(b.id))
    if (q.provider) list = list.filter((i) => prvOf(i.provider_id)?.slug === q.provider)
    if (q.category) list = list.filter((i) => catOf(i.category_id)?.key === q.category || i.category_id === q.category)
    if (q.verified === 'true') list = list.filter((i) => i.is_verified)
    if (q.search) {
      const s = q.search.toLowerCase()
      list = list.filter((i) => i.name.toLowerCase().includes(s) || (i.headline || '').toLowerCase().includes(s))
    }
    // Joined here so a 24-card grid isn't an N+1 of lookups in the browser.
    const out = list.map((i) => ({ ...i, category: catOf(i.category_id), provider: prvOf(i.provider_id) }))
    return ok(paginate(out, { per_page: 24, ...q }))
  }],
  ['GET', /^\/instructors\/([^/]+)$/, ({ m }) => {
    // is_active enforced here too, matching InstructorDetail in catalog.go:
    // hiding an expert from the directory but leaving their page reachable by
    // URL isn't hiding them.
    const i = db.instructors.find((x) => (x.slug === m[1] || x.id === m[1]) && x.is_active !== false)
    if (!i) fail(404, 'Pakar tidak ditemukan')
    return ok({
      ...i,
      provider: prvOf(i.provider_id),
      category: catOf(i.category_id),
      courses: ordered(db.courses.filter((c) => c.instructor_id === i.id && c.status === 'published')).map(decorate),
      slots: db.consultationSlots.filter((s) => s.instructor_id === i.id && !s.is_booked),
      answered_count: db.qaQuestions.filter((qq) => qq.instructor_id === i.id && qq.status === 'answered').length,
    })
  }],
  ['GET', /^\/courses$/, ({ q }) => {
    let list = ordered(db.courses.filter((c) => c.status === 'published'))
    if (q.search) {
      const s = q.search.toLowerCase()
      list = list.filter((c) => c.title.toLowerCase().includes(s) || insOf(c.instructor_id).name.toLowerCase().includes(s) || catOf(c.category_id).name_id.toLowerCase().includes(s))
    }
    if (q.category) list = list.filter((c) => catOf(c.category_id)?.key === q.category)
    if (q.provider) list = list.filter((c) => prvOf(c.provider_id)?.slug === q.provider)
    if (q.level) list = list.filter((c) => c.level === q.level)
    if (q.price === 'free') list = list.filter((c) => c.is_free)
    if (q.price === 'paid') list = list.filter((c) => !c.is_free)
    if (q.language) list = list.filter((c) => c.language === q.language)
    if (q.min_rating) list = list.filter((c) => c.rating_avg >= +q.min_rating)
    if (q.features) {
      const f = String(q.features).split(',')
      list = list.filter((c) => f.every((x) => (x === 'certificate' ? c.certificate_enabled : x === 'quiz' ? db.lessons.some((l) => l.course_id === c.id && l.type === 'quiz') : x === 'live' ? db.lessons.some((l) => l.course_id === c.id && l.type === 'live') : true)))
    }
    const sorters = {
      most_popular: (a, b) => b.enroll_count - a.enroll_count,
      highest_rated: (a, b) => b.rating_avg - a.rating_avg,
      newest: (a, b) => new Date(b.published_at) - new Date(a.published_at),
      // price_credits, bukan price: kolom rupiah warisan selalu 0 untuk kelas
      // berharga kredit, jadi mengurutkannya tidak mengubah apa pun.
      price_low: (a, b) => (a.price_credits || 0) - (b.price_credits || 0),
      price_high: (a, b) => (b.price_credits || 0) - (a.price_credits || 0),
    }
    list.sort(sorters[q.sort] || sorters.most_popular)
    const page = paginate(list, { per_page: 12, ...q })
    return ok({ ...page, items: page.items.map(decorate) })
  }],
  ['GET', /^\/courses\/([^/]+)\/curriculum$/, ({ m }) => {
    const c = visibleCourse(m[1])
    const enrolled = db.enrollments.some((e) => e.course_id === c.id)
    return ok(curriculumOf(c.id, { unlocked: enrolled }))
  }],
  ['GET', /^\/courses\/([^/]+)\/reviews$/, ({ m, q }) => {
    const c = visibleCourse(m[1])
    const list = db.reviews.filter((r) => r.course_id === c.id && r.status === 'published')
    const dist = [5, 4, 3, 2, 1].map((s) => ({ stars: s, count: list.filter((r) => r.stars === s).length }))
    return ok({ ...paginate(list, { per_page: 6, ...q }), distribution: dist })
  }],
  ['GET', /^\/courses\/([^/]+)$/, ({ m }) => {
    const c = visibleCourse(m[1])
    const enrolled = db.enrollments.some((e) => e.course_id === c.id)
    return ok({
      ...decorate(c),
      curriculum: curriculumOf(c.id, { unlocked: enrolled }),
      instructor_full: insOf(c.instructor_id),
      related: ordered(db.courses.filter((x) => x.category_id === c.category_id && x.id !== c.id && x.status === 'published')).slice(0, 4).map(decorate),
      review_summary: { avg: c.rating_avg, count: c.review_count },
      // Prasyarat + jalur belajar, cermin CourseDetail di Go. `met` di mock
      // dibaca dari enrollment yang ada; tanpa ini varian "sudah lulus" —
      // yaitu satu-satunya keadaan yang MEMBUKA tombol beli — tidak pernah
      // bisa dilihat saat meninjau desain.
      prereq: prereqInfo(c),
      next_courses: ordered(db.courses.filter((x) => x.prereq_course_id === c.id && x.status === 'published')).slice(0, 6).map(decorate),
    })
  }],
  ['GET', /^\/search$/, ({ q }) => {
    const s = String(q.q || '').toLowerCase()
    if (!s) return ok({ courses: [], instructors: [], categories: [] })
    return ok({
      courses: ordered(db.courses.filter((c) => c.status === 'published' && c.title.toLowerCase().includes(s))).slice(0, 6).map(decorate),
      instructors: db.instructors.filter((i) => i.name.toLowerCase().includes(s) && i.is_active !== false).slice(0, 4),
      categories: db.categories.filter((c) => c.name_id.toLowerCase().includes(s) || c.name_en.toLowerCase().includes(s)).slice(0, 4),
    })
  }],
  ['GET', /^\/home$/, () => ok({
    // Hanya kelas terbit yang boleh jadi wajah beranda; null kalau belum ada
    // satu pun. Mirrors Home() di catalog.go.
    hero_course: (() => {
      const pub = db.courses.filter((c) => c.status === 'published')
      if (!pub.length) return null
      return decorate(pub.reduce((a, b) => (b.enroll_count > a.enroll_count ? b : a)))
    })(),
    categories: db.categories.filter((c) => c.is_active),
    featured: ordered(db.courses.filter((c) => c.status === 'published')).slice(0, 8).map(decorate),
    // Array.sort JS stabil, jadi kelas yang enroll_count-nya seri (di produksi:
    // hampir semuanya, karena nol) jatuh kembali ke urutan kanonik, bukan ke acak.
    popular: ordered(db.courses.filter((c) => c.status === 'published')).sort((a, b) => b.enroll_count - a.enroll_count).slice(0, 8).map(decorate),
    free: ordered(db.courses.filter((c) => c.is_free && c.status === 'published')).map(decorate),
    providers: db.providers.filter((p) => p.is_active),
    // Active, verified-first — not verified-only. Every imported expert starts
    // unverified by design, so gating on is_verified would empty this strip.
    // Mirrors Home() in catalog.go.
    instructors: [...db.instructors].filter((i) => i.is_active !== false)
      .sort((a, b) => Number(b.is_verified) - Number(a.is_verified) || (b.student_count || 0) - (a.student_count || 0))
      .slice(0, 6),
    live_now: db.liveClasses.filter((l) => l.status === 'live' || l.status === 'scheduled').slice(0, 3),
    stats: { learners: 31_740, courses: db.courses.filter((c) => c.status === 'published').length, experts: db.instructors.filter((i) => i.is_active !== false).length, certificates: 2_180 },
  })],

  /* ---- learner ---- */
  ['GET', /^\/me\/courses$/, () => ok(db.enrollments.map((e) => {
    const c = db.courses.find((x) => x.id === e.course_id)
    const ls = db.lessons.filter((l) => l.course_id === c.id)
    const next = ls.find((l) => !db.lessonProgress.find((p) => p.lesson_id === l.id)?.completed) || ls[0]
    return { ...e, course: decorate(c), next_lesson: next, lesson_total: ls.length, lesson_done: db.lessonProgress.filter((p) => p.enrollment_id === e.id && p.completed).length }
  }))],
  ['POST', /^\/courses\/([^/]+)\/enroll$/, ({ m }) => {
    const c = db.courses.find((x) => x.id === m[1] || x.slug === m[1])
    // Terbit tanpa kecuali — lebih ketat dari visibleCourse: admin boleh membaca
    // draf, tapi mendaftar ke kelas yang harganya pun belum final tidak masuk akal.
    if (!c || c.status !== 'published') fail(404, 'Kelas tidak ditemukan')
    if (db.enrollments.some((e) => e.course_id === c.id)) fail(409, 'Anda sudah terdaftar di kelas ini')
    if (!c.is_free) fail(402, 'Kelas berbayar — buat order dulu', { requires_purchase: true, price_credits: c.price_credits })
    const e = { id: uidNew('enr'), user_id: seed.users[0].id, course_id: c.id, source: 'free', order_id: null, enrolled_at: new Date().toISOString(), expires_at: null, started_at: null, completed_at: null, percent_complete: 0, status: 'active', certificate_id: null }
    db.enrollments.push(e)
    db.lessons.filter((l) => l.course_id === c.id).forEach((l) => db.lessonProgress.push({ id: uidNew('lpr'), enrollment_id: e.id, lesson_id: l.id, user_id: e.user_id, last_position_sec: 0, seconds_watched: 0, percent_watched: 0, completed: false, completed_at: null }))
    persist()
    return ok(e, 'Berhasil mendaftar')
  }],
  ['GET', /^\/lessons\/([^/]+)$/, ({ m }) => {
    const l = db.lessons.find((x) => x.id === m[1])
    if (!l) fail(404, 'Lesson tidak ditemukan')
    const c = db.courses.find((x) => x.id === l.course_id)
    const e = db.enrollments.find((x) => x.course_id === l.course_id)
    if (!e && !l.is_preview) fail(403, 'Anda belum terdaftar di kelas ini')
    const p = db.lessonProgress.find((x) => x.lesson_id === l.id)
    return ok({
      ...l,
      course: decorate(c),
      curriculum: curriculumOf(c.id, { unlocked: !!e }),
      quiz: l.quiz_id ? db.quizzes.find((q) => q.id === l.quiz_id) : null,
      progress: p || null,
      // `has_video` WAJIB ada di sini juga. Pemutar memutuskan antara
      // <video> dan "belum diunggah" dari kunci ini; kalau mock tidak
      // mengirimnya, setiap pelajaran di build statis yang ter-deploy tampil
      // sebagai pelajaran kosong — dan itulah build tempat desain ditinjau.
      play: {
        hls_url: c.promo_video_url,
        has_video: !!c.promo_video_url,
        content_lang: c.language || 'id',
        resume_at: p?.last_position_sec || 0,
        watermark: `${seed.users[0].email} · ${seed.users[0].id}`,
        // Umur URL. Pemutar memakainya untuk meminta tautan baru SEBELUM yang
        // lama mati. Sengaja sependek produksi: kalau di sini panjang, jalur
        // penyegaran tidak pernah berjalan selama pengembangan, dan kali
        // pertamanya dicoba adalah di staging.
        expires_in: 180,
      },
      attempts: db.quizAttempts.filter((a) => a.quiz_id === l.quiz_id),
      // Materi unduhan. Di server ini adalah URL bertanda tangan berumur
      // pendek — bukan kunci /media publik, karena lembar kerja kelas berbayar
      // tidak boleh bisa diunduh siapa pun yang menerima tautannya. Yang ditiru
      // di sini cuma BENTUKNYA; mock bukan batas keamanan.
      resources: (l.resources || []).map((r) => ({ ...r, url: r.url || '#' })),
    })
  }],
  // Penerbitan ulang URL pemutaran. Bentuknya ditiru dari PlayToken di Go,
  // termasuk `expires_in`, supaya pemutar menjalankan jalur penyegaran yang
  // sama persis di dev dan di produksi. Tanpa rute ini mock membalas 404, dan
  // pemutar menampilkan "tautan tidak bisa diperbarui" — kegagalan yang hanya
  // ada di dev, yaitu jenis yang paling membuang waktu untuk ditelusuri.
  ['POST', /^\/lessons\/([^/]+)\/play-token$/, ({ m }) => {
    const l = db.lessons.find((x) => x.id === m[1])
    if (!l) fail(404, 'Lesson tidak ditemukan')
    const c = db.courses.find((x) => x.id === l.course_id)
    const p = db.lessonProgress.find((x) => x.lesson_id === l.id)
    return ok({
      hls_url: c.promo_video_url,
      has_video: !!c.promo_video_url,
      content_lang: c.language || 'id',
      resume_at: p?.last_position_sec || 0,
      expires_in: 180,
      watermark: `${seed.users[0].email} · ${seed.users[0].id}`,
    })
  }],
  // --- catatan murid ---
  //
  // Disimpan di `db` (bukan localStorage) supaya bentuknya sama dengan server:
  // punya id, bertanda waktu video, dan bisa dihapus satu per satu. Kalau mock
  // memakai satu string catatan seperti versi lama, panel catatan yang baru
  // tidak akan bisa ditinjau sama sekali di build statis yang ter-deploy —
  // dan itulah build tempat desainnya dilihat orang.
  ['GET', /^\/lessons\/([^/]+)\/notes$/, ({ m }) =>
    ok(db.lessonNotes.filter((n) => n.lesson_id === m[1]).sort((a, b) => a.at_sec - b.at_sec))],
  ['POST', /^\/lessons\/([^/]+)\/notes$/, ({ m, body }) => {
    const l = db.lessons.find((x) => x.id === m[1])
    if (!l) fail(404, 'Lesson tidak ditemukan')
    if (!String(body.body || '').trim()) fail(400, 'Catatan tidak boleh kosong.')
    const n = {
      id: uidNew('nte'), lesson_id: l.id, course_id: l.course_id, user_id: db.currentUserId,
      at_sec: Math.max(0, body.at_sec || 0), body: String(body.body).trim(),
      created_at: new Date().toISOString(),
    }
    db.lessonNotes.push(n)
    return ok(n)
  }],
  ['PUT', /^\/notes\/([^/]+)$/, ({ m, body }) => {
    const n = db.lessonNotes.find((x) => x.id === m[1])
    if (!n) fail(404, 'Catatan tidak ditemukan')
    if (!String(body.body || '').trim()) fail(400, 'Catatan tidak boleh kosong.')
    n.body = String(body.body).trim() // at_sec sengaja tidak ikut, sama dengan server
    return ok(n)
  }],
  ['DELETE', /^\/notes\/([^/]+)$/, ({ m }) => {
    const i = db.lessonNotes.findIndex((x) => x.id === m[1])
    if (i < 0) fail(404, 'Catatan tidak ditemukan')
    db.lessonNotes.splice(i, 1)
    return ok({ id: m[1] }, 'Catatan dihapus')
  }],
  ['POST', /^\/lessons\/([^/]+)\/progress$/, ({ m, body }) => {
    const p = db.lessonProgress.find((x) => x.lesson_id === m[1])
    if (!p) fail(404, 'Progress tidak ditemukan')
    const l = db.lessons.find((x) => x.id === m[1])
    p.last_position_sec = body.last_position_sec ?? p.last_position_sec
    p.seconds_watched = Math.max(p.seconds_watched, body.seconds_watched ?? 0)
    if (l.duration_sec) p.percent_watched = Math.min(100, Math.round((p.last_position_sec / l.duration_sec) * 100))
    if (p.percent_watched >= 90 && !p.completed) { p.completed = true; p.completed_at = new Date().toISOString() }
    const e = recalcEnrollment(p.enrollment_id)
    return ok({ progress: p, enrollment: e })
  }],
  ['POST', /^\/lessons\/([^/]+)\/complete$/, ({ m }) => {
    const p = db.lessonProgress.find((x) => x.lesson_id === m[1])
    if (!p) fail(404, 'Progress tidak ditemukan')
    p.completed = true; p.percent_watched = 100; p.completed_at = new Date().toISOString()
    const e = recalcEnrollment(p.enrollment_id)
    return ok({ progress: p, enrollment: e })
  }],
  ['POST', /^\/quizzes\/([^/]+)\/attempts$/, ({ m, body }) => {
    const quiz = db.quizzes.find((q) => q.id === m[1])
    if (!quiz) fail(404, 'Kuis tidak ditemukan')
    let earned = 0, total = 0
    const detail = quiz.questions.map((q) => {
      total += q.points
      const correctIds = q.options.filter((o) => o.is_correct).map((o) => o.id).sort()
      const given = [...(body.answers?.[q.id] || [])].sort()
      const isCorrect = correctIds.length === given.length && correctIds.every((v, i) => v === given[i])
      if (isCorrect) earned += q.points
      return { question_id: q.id, is_correct: isCorrect, correct_option_ids: correctIds, points_awarded: isCorrect ? q.points : 0, explanation: q.explanation }
    })
    const pct = Math.round((earned / total) * 100)
    const attempt = {
      id: uidNew('att'), quiz_id: quiz.id, user_id: seed.users[0].id, attempt_number: db.quizAttempts.filter((a) => a.quiz_id === quiz.id).length + 1,
      started_at: body.started_at || new Date().toISOString(), submitted_at: new Date().toISOString(),
      time_spent_sec: body.time_spent_sec || 0, score: earned, score_pct: pct, passed: pct >= quiz.passing_score,
      status: 'graded', detail,
    }
    db.quizAttempts.push(attempt)
    if (attempt.passed) {
      const p = db.lessonProgress.find((x) => x.lesson_id === quiz.lesson_id)
      if (p) { p.completed = true; p.percent_watched = 100; p.completed_at = new Date().toISOString(); recalcEnrollment(p.enrollment_id) }
    }
    persist()
    return ok(attempt)
  }],
  ['GET', /^\/me\/certificates$/, () => ok(db.certificates)],
  ['GET', /^\/me\/orders$/, () => ok(db.orders.map((o) => ({ ...o, provider_name: prvOf(o.provider_id)?.name })))],
  ['GET', /^\/me\/bookings$/, () => ok(db.bookings.map((b) => ({
    ...b, instructor: insOf(b.instructor_id), slot: db.consultationSlots.find((s) => s.id === b.slot_id),
  })))],
  ['GET', /^\/me\/notifications$/, () => ok(db.notifications)],
  ['POST', /^\/me\/notifications\/read$/, () => { db.notifications.forEach((n) => { n.read_at = n.read_at || new Date().toISOString() }); persist(); return ok(db.notifications) }],

  /* ---- self-service akun (padanan internal/api/me.go) ---- */
  // Were these missing, anyone without a .env.local — CI, a fresh clone, and the
  // deployed static build, which all default to VITE_USE_MOCK=true — would open
  // /me/profile and get a red "Mock route belum ada" error.
  // `POST /me/password` dihapus di sini juga — password diurus The Muslim.
  ['GET', /^\/me\/instructor$/, () => ok(myInstructor())],


  // ——— video pelajaran (admin) ———
  //
  // Unggahan sungguhan mustahil ditiru di sini: ia menandatangani URL S3 dan
  // mengirim byte langsung dari peramban. Mock menjawab 503 dengan kalimat yang
  // jujur, bukan berpura-pura berhasil — kotak unggah yang "berhasil" tapi tidak
  // menghasilkan video akan membuat orang mencari bug di tempat yang salah.
  ['GET', /^\/admin\/lessons\/([^/]+)\/video$/, ({ m }) => {
    const l = db.lessons.find((x) => x.id === m[1]) || {}
    return ok({
      video_key: l.video_key || '', video_status: l.video_status || '',
      video_bytes: l.video_bytes || 0, video_mime: l.video_mime || '',
      video_orig_name: l.video_orig_name || '', video_faststart: null,
      video_error: '', video_uploaded_at: null,
      duration_sec: l.duration_sec || 0, play_url: '',
    })
  }],
  ['POST', /^\/admin\/lessons\/([^/]+)\/video\/(init|complete|abort)$/, () =>
    fail(503, 'Unggah video butuh backend sungguhan. Jalankan API di :8700 dan set VITE_USE_MOCK=false.')],
  // Materi unduhan. TIDAK dijawab 503 seperti video: byte-nya memang lewat API
  // (batasnya 25 MB), jadi bentuk alurnya bisa ditiru utuh di sini — dan yang
  // ingin ditinjau di build statis justru daftarnya, bukan unggahannya.
  // `api.upload` sendiri punya cabang mock yang tidak menyentuh jaringan, jadi
  // rute POST ini hanya terpakai kalau suatu saat cabang itu dilepas; ia ada
  // supaya paritasnya tidak berlubang saat itu terjadi.
  ['POST', /^\/admin\/lessons\/([^/]+)\/resources$/, ({ m }) => {
    const l = db.lessons.find((x) => x.id === m[1])
    if (!l) fail(404, 'Lesson tidak ditemukan')
    const r = { id: uidNew('res'), name: 'Materi.pdf', url: '#', bytes: 120000, mime: 'application/pdf' }
    l.resources = [...(l.resources || []), r]
    return ok({ resource: r }, 'Materi ditambahkan')
  }],
  ['DELETE', /^\/admin\/lessons\/([^/]+)\/resources\/([^/]+)$/, ({ m }) => {
    const l = db.lessons.find((x) => x.id === m[1])
    if (!l) fail(404, 'Lesson tidak ditemukan')
    const before = (l.resources || []).length
    l.resources = (l.resources || []).filter((r) => r.id !== m[2])
    if (l.resources.length === before) fail(404, 'Materi tidak ditemukan')
    return ok({ id: m[2] }, 'Materi dihapus')
  }],
  ['DELETE', /^\/admin\/lessons\/([^/]+)\/video$/, ({ m }) => {
    const l = db.lessons.find((x) => x.id === m[1])
    if (l) { l.video_key = ''; l.video_status = ''; persist() }
    return ok({ ok: true }, 'Video dihapus')
  }],

  // ——— kurasi ———
  ['GET', /^\/admin\/courses\/([^/]+)\/readiness$/, ({ m }) => {
    const c = db.courses.find((x) => x.id === m[1] || x.slug === m[1])
    if (!c) fail(404, 'Kelas tidak ditemukan')
    const problems = []
    if ((c.description || '').length < 80) problems.push({ key: 'description', detail: 'Deskripsi kelas masih terlalu singkat.' })
    if (!c.thumbnail_url) problems.push({ key: 'thumbnail', detail: 'Kelas belum punya gambar sampul.' })
    if (!(c.objectives || []).some((o) => String(o).trim())) problems.push({ key: 'objectives', detail: 'Tujuan belajar belum diisi.' })
    if (!db.lessons.some((l) => l.course_id === c.id)) problems.push({ key: 'lessons', detail: 'Kelas belum punya satu pun pelajaran.' })
    if (!c.is_free && !(c.price_credits > 0)) problems.push({ key: 'price', detail: 'Harga belum ditetapkan dalam kredit.' })
    const submittable = c.status === 'draft' || c.status === 'rejected'
    return ok({
      status: c.status, ready: problems.length === 0, problems,
      can_submit: submittable && problems.length === 0,
      can_edit_price: submittable,
    })
  }],
  ['POST', /^\/admin\/courses\/([^/]+)\/submit$/, ({ m }) => {
    const c = db.courses.find((x) => x.id === m[1] || x.slug === m[1])
    if (!c) fail(404, 'Kelas tidak ditemukan')
    if (!(c.status === 'draft' || c.status === 'rejected')) fail(422, 'Kelas ini tidak perlu diajukan lagi.')
    c.status = 'pending_review'; c.rejected_reason = null
    persist()
    return ok(c, 'Kelas diajukan untuk kurasi')
  }],
  // Menata urutan kelas. Cermin AdminCourseMove di internal/api/course_order.go:
  // himpunannya per PAKAR, dan SELURUH himpunan dinomori ulang 1..n — bukan cuma
  // dua baris yang bertukar. Kalau mock hanya menukar dua angka, nomor berlubang
  // cuma akan muncul di API sungguhan, yaitu tempat yang paling sulit diperiksa.
  ['POST', /^\/admin\/courses\/([^/]+)\/move$/, ({ m, body }) => {
    const c = db.courses.find((x) => x.id === m[1] || x.slug === m[1])
    if (!c) fail(404, 'Kelas tidak ditemukan')
    const dir = body?.dir
    const hasTo = typeof body?.to === 'number'
    if (!hasTo && dir !== 'up' && dir !== 'down') {
      fail(400, 'Kirim "to" (posisi tujuan) atau "dir" ("up"/"down").')
    }

    const peers = ordered(db.courses.filter((x) => x.instructor_id === c.instructor_id))
    const at = peers.findIndex((x) => x.id === c.id)
    if (at >= 0 && hasTo) {
      // Seret-lepas: PINDAH, bukan tukar — cermin moveToPos di Go. Kalau mock
      // menukar sementara server menggeser, seretan jauh akan tampil benar di
      // sini dan salah di produksi, yaitu tempat yang paling sulit diperiksa.
      const dst = Math.min(Math.max(body.to - 1, 0), peers.length - 1)
      peers.splice(dst, 0, peers.splice(at, 1)[0])
    } else {
      const to = dir === 'up' ? at - 1 : at + 1
      // Sudah di ujung: tidak terjadi apa pun, dan itu BUKAN galat.
      if (at >= 0 && to >= 0 && to < peers.length) {
        ;[peers[at], peers[to]] = [peers[to], peers[at]]
      }
    }
    peers.forEach((x, i) => { x.position = i + 1 })
    persist()
    return ok({ order: peers.map((x) => ({ id: x.id, position: x.position })) })
  }],


  // ——— setelan pemberitahuan email ———
  //
  // `smtp_ready: false` disengaja, dan itu keadaan yang JUJUR: mock berjalan di
  // peramban dan tidak punya SMTP sama sekali. Menjawab `true` akan membuat
  // tombol "Kirim email percobaan" hidup lalu melaporkan sukses tanpa satu pun
  // email — dan itu persis kesalahan yang paling ingin dicegah oleh spanduk
  // status di halaman itu.
  ['GET', /^\/admin\/settings\/notifications$/, () => ok({
    settings: mockNotify(), smtp_ready: false, smtp_from: '',
  })],
  ['PUT', /^\/admin\/settings\/notifications$/, ({ body }) => {
    const next = { ...mockNotify(), ...body }
    next.curation_emails = cleanEmails(next.curation_emails)
    if (next.curation_emails.length > 20) fail(400, 'Maksimal 20 alamat penerima.')
    // Validasi yang sama dengan Go. Kalau mock membiarkannya lolos, bug
    // "menyala tapi tanpa penerima" cuma muncul di server sungguhan.
    if (next.curation_enabled && next.curation_emails.length === 0) {
      fail(400, 'Isi minimal satu alamat email, atau matikan pemberitahuan kurasi.')
    }
    db.notify = next; persist()
    return ok({ settings: next, smtp_ready: false, smtp_from: '' }, 'Setelan pemberitahuan disimpan')
  }],
  ['POST', /^\/admin\/settings\/notifications\/test$/, () => {
    fail(422, 'SMTP belum dikonfigurasi di server (SMTP_HOST dan SMTP_FROM masih kosong).')
  }],

  ['GET', /^\/me\/onboarding$/, () => ok({
    onboarding_guide_read: !!db.onboardingGuideRead, onboarding_hidden: !!db.onboardingHidden,
  })],
  ['PATCH', /^\/me\/onboarding$/, ({ body }) => {
    if (body.onboarding_guide_read !== undefined) db.onboardingGuideRead = !!body.onboarding_guide_read
    if (body.onboarding_hidden !== undefined) db.onboardingHidden = !!body.onboarding_hidden
    persist()
    return ok({ onboarding_guide_read: !!db.onboardingGuideRead, onboarding_hidden: !!db.onboardingHidden })
  }],

  ['POST', /^\/courses\/([^/]+)\/reviews$/, ({ m, body }) => {
    const c = db.courses.find((x) => x.id === m[1] || x.slug === m[1])
    if (!db.enrollments.some((e) => e.course_id === c.id)) fail(403, 'Hanya peserta kelas yang bisa memberi ulasan')
    const r = { id: uidNew('rev'), course_id: c.id, course_title: c.title, user_id: seed.users[0].id, user_name: seed.users[0].name, user_avatar: seed.users[0].avatar_url, stars: body.stars, text: body.text, helpful_count: 0, status: 'published', created_at: new Date().toISOString() }
    db.reviews.unshift(r); persist()
    return ok(r, 'Ulasan terkirim')
  }],
  ['GET', /^\/verify\/([^/]+)$/, ({ m }) => {
    const c = db.certificates.find((x) => x.verification_code.toLowerCase() === String(m[1]).toLowerCase() || x.serial_number.toLowerCase() === String(m[1]).toLowerCase())
    if (!c) fail(404, 'Sertifikat tidak ditemukan. Periksa kembali kodenya.')
    return ok(c)
  }],

  /* ---- checkout ---- */
  ['POST', /^\/coupons\/validate$/, ({ body }) => {
    const c = db.coupons.find((x) => x.code.toLowerCase() === String(body.code || '').toLowerCase())
    if (!c) fail(404, 'Kode kupon tidak dikenal')
    if (!c.is_active || c.uses_count >= c.max_uses) fail(422, 'Kupon sudah tidak berlaku')
    return ok(c, 'Kupon diterapkan')
  }],
  ['POST', /^\/orders$/, ({ body }) => {
    const c = db.courses.find((x) => x.id === body.course_id || x.slug === body.course_id)
    // Aturan sama dengan enroll: tidak ada order untuk kelas yang belum terbit.
    if (!c || c.status !== 'published') fail(404, 'Kelas tidak ditemukan')
    const coupon = body.coupon ? db.coupons.find((x) => x.code.toLowerCase() === body.coupon.toLowerCase() && x.is_active) : null
    // Kupon nilai tetap memakai `value_credits`, BUKAN `value`.
    //
    // Kolom `value` untuk kupon fixed adalah rupiah warisan. Membacanya di sini
    // membuat kupon "potongan Rp 30.000" berarti potongan 30.000 kredit — dan
    // karena kelas termahal berharga puluhan kredit, kupon itu menggratiskan
    // seluruh katalog. Potongannya juga dibatasi harga: satu kupon salah ketik
    // tidak boleh cukup untuk membuat setiap pesanan bernilai nol.
    const harga = c.price_credits || 0
    const potongan = coupon
      ? (coupon.type === 'percent'
        ? Math.floor((harga * coupon.value) / 100)
        : (coupon.value_credits || 0))
      : 0
    const discount = Math.min(potongan, harga)
    const o = {
      id: uidNew('ord'), user_id: seed.users[0].id, provider_id: c.provider_id, course_id: c.id, currency: 'CREDIT',
      subtotal: 0, discount_total: discount, tax_total: 0, gross_amount: 0,
      credits_total: Math.max(0, harga - discount),
      coupon_id: coupon?.id || null, gateway: 'credits',
      gateway_order_id: `KX-${Date.now().toString().slice(-8)}`, gateway_txn_id: null,
      status: 'pending', paid_at: null,
      lines: [{ kind: 'course', target_id: c.id, title_snapshot: c.title, price_snapshot_credits: harga, qty: 1 }],
      attribution_channel: coupon ? 'coupon' : 'organic',
      // Tidak ada snap_token / redirect Midtrans. Penyedia pembayaran belum
      // dipilih; pembelian kelas memakai saldo kredit, dan top-up memakai
      // gerbang manual. Mock yang berpura-pura punya halaman bayar akan
      // membuat alur yang belum ada tampak sudah jadi.
    }
    db.orders.unshift(o); persist()
    return ok(o, 'Order dibuat')
  }],
  ['GET', /^\/orders\/([^/]+)$/, ({ m }) => {
    const o = db.orders.find((x) => x.id === m[1])
    if (!o) fail(404, 'Order tidak ditemukan')
    return ok(o)
  }],

  /* ---- live / qa / consultation ---- */
  ['GET', /^\/live-classes$/, ({ q }) => {
    let list = db.liveClasses.map((l) => ({ ...l, instructor: insOf(l.instructor_id), provider: prvOf(l.provider_id) }))
    if (q.status) list = list.filter((l) => l.status === q.status)
    list.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
    return ok(list)
  }],
  ['GET', /^\/live-classes\/([^/]+)$/, ({ m }) => {
    const l = db.liveClasses.find((x) => x.id === m[1])
    if (!l) fail(404, 'Kelas langsung tidak ditemukan')
    return ok({ ...l, instructor: insOf(l.instructor_id), provider: prvOf(l.provider_id), course: l.course_id ? decorate(db.courses.find((c) => c.id === l.course_id)) : null })
  }],
  ['POST', /^\/live-classes\/([^/]+)\/remind$/, ({ m }) => {
    const l = db.liveClasses.find((x) => x.id === m[1]); l.reminded = !l.reminded; persist(); return ok(l)
  }],
  ['POST', /^\/live-classes\/([^/]+)\/join-token$/, ({ m }) => ok({ room: m[1], token: `livekit.mock.${Math.random().toString(36).slice(2)}`, url: 'wss://livekit.the-muslim.app' })],
  ['GET', /^\/qa\/questions$/, ({ q }) => {
    let list = db.qaQuestions.map((x) => ({ ...x, instructor: insOf(x.instructor_id), category: catOf(x.category_id) }))
    if (q.status) list = list.filter((x) => x.status === q.status)
    if (q.category) list = list.filter((x) => x.category?.key === q.category)
    if (q.search) list = list.filter((x) => x.title.toLowerCase().includes(q.search.toLowerCase()))
    return ok(paginate(list, { per_page: 10, ...q }))
  }],
  ['POST', /^\/qa\/questions$/, ({ body }) => {
    const qq = { id: uidNew('qa'), provider_id: db.providers[0].id, user_id: seed.users[0].id, user_name: seed.users[0].name, category_id: body.category_id, instructor_id: body.instructor_id || null, title: body.title, body: body.body, status: 'open', is_public: body.is_public ?? true, vote_count: 0, created_at: new Date().toISOString(), answers: [] }
    db.qaQuestions.unshift(qq); persist()
    return ok(qq, 'Pertanyaan terkirim — pakar akan menjawab dalam 1–3 hari kerja')
  }],
  ['GET', /^\/qa\/questions\/([^/]+)$/, ({ m }) => {
    const qq = db.qaQuestions.find((x) => x.id === m[1])
    if (!qq) fail(404, 'Pertanyaan tidak ditemukan')
    return ok({ ...qq, instructor: insOf(qq.instructor_id), category: catOf(qq.category_id), related: db.qaQuestions.filter((x) => x.category_id === qq.category_id && x.id !== qq.id).slice(0, 4) })
  }],
  ['GET', /^\/consultations$/, () => ok(db.instructors.filter((i) => i.is_active !== false).slice(0, 8).map((i) => ({
    ...i, category: catOf(i.category_id), provider: prvOf(i.provider_id),
    next_slot: db.consultationSlots.filter((s) => s.instructor_id === i.id && !s.is_booked)[0] || null,
    slot_count: db.consultationSlots.filter((s) => s.instructor_id === i.id && !s.is_booked).length,
    from_price: Math.min(...db.consultationSlots.filter((s) => s.instructor_id === i.id).map((s) => s.price)),
  })))],
  ['GET', /^\/instructors\/([^/]+)\/slots$/, ({ m }) => {
    const i = db.instructors.find((x) => x.id === m[1] || x.slug === m[1])
    return ok(db.consultationSlots.filter((s) => s.instructor_id === i.id))
  }],
  ['POST', /^\/bookings$/, ({ body }) => {
    const s = db.consultationSlots.find((x) => x.id === body.slot_id)
    if (!s) fail(404, 'Slot tidak ditemukan')
    if (s.is_booked) fail(409, 'Slot baru saja dibooking orang lain')
    s.is_booked = true
    const b = { id: uidNew('bkg'), slot_id: s.id, user_id: seed.users[0].id, instructor_id: s.instructor_id, status: 'confirmed', order_id: null, room: uidNew('room'), notes: body.notes || '', created_at: new Date().toISOString() }
    db.bookings.unshift(b); persist()
    return ok(b, 'Booking dikonfirmasi')
  }],

  /* ---- admin ---- */
  ['GET', /^\/admin\/dashboard$/, () => ok({
    ...seed.dashboardStats,
    approval_queue: db.courses.filter((c) => c.status === 'pending_review').map(decorate),
    recent_orders: db.orders.slice(0, 6).map((o) => ({ ...o, provider_name: prvOf(o.provider_id)?.name })),
    flagged_reviews: db.reviews.filter((r) => r.stars <= 3).slice(0, 5),
  })],
  ['GET', /^\/admin\/courses$/, ({ q }) => {
    let list = ordered(db.courses).map(decorate)
    if (q.status) list = list.filter((c) => c.status === q.status)
    if (q.provider) list = list.filter((c) => c.provider.slug === q.provider)
    if (q.search) list = list.filter((c) => c.title.toLowerCase().includes(q.search.toLowerCase()))
    return ok(paginate(list, { per_page: 10, ...q }))
  }],
  ['POST', /^\/admin\/courses\/bulk$/, ({ body }) => {
    const { ids = [], action, reason } = body
    ids.forEach((id) => {
      const c = db.courses.find((x) => x.id === id)
      if (!c) return
      if (action === 'approve') { c.status = 'published'; c.published_at = new Date().toISOString(); c.rejected_reason = null }
      if (action === 'reject') { c.status = 'rejected'; c.rejected_reason = reason || 'Tidak memenuhi standar kurasi.' }
      if (action === 'archive') c.status = 'archived'
      if (action === 'unpublish') c.status = 'draft'
      if (action === 'delete') db.courses = db.courses.filter((x) => x.id !== id)
    })
    persist()
    return ok({ affected: ids.length }, `${ids.length} kelas diperbarui`)
  }],
  // Editor admin memakai rute ini, bukan `/courses/:slug` — rute publik menolak
  // kelas belum terbit, dan draf justru isi utama meja editor.
  ['GET', /^\/admin\/courses\/([^/]+)$/, ({ m }) => {
    const c = db.courses.find((x) => x.id === m[1] || x.slug === m[1])
    if (!c) fail(404, 'Kelas tidak ditemukan')
    return ok(decorate(c))
  }],
  ['PUT', /^\/admin\/courses\/([^/]+)$/, ({ m, body }) => {
    const c = db.courses.find((x) => x.id === m[1])
    if (!c) fail(404, 'Kelas tidak ditemukan')
    // Daftar putih, menyalin `courseUpdatable` di internal/api/admin.go. Dulu
    // di sini `Object.assign(c, body)` — apa pun yang dikirim masuk, jadi mock
    // selalu "berhasil" sementara API sungguhan menolak payload yang sama
    // dengan 422. Bug simpan-objective itu karena itu tidak pernah terlihat
    // selama VITE_USE_MOCK=true.
    const patch = {}
    for (const [k, v] of Object.entries(body || {})) {
      if (!ADMIN_COURSE_FIELDS.has(k)) continue
      patch[k] = LIST_FIELDS.has(k)
        ? (Array.isArray(v) ? v.map((s) => String(s).trim()).filter(Boolean) : [])
        : v
    }
    if (!Object.keys(patch).length) fail(400, 'Tidak ada kolom yang bisa diperbarui.')
    if (patch.status === 'published' && !c.published_at) patch.published_at = new Date().toISOString()
    Object.assign(c, patch, { updated_at: new Date().toISOString() }); persist()
    return ok(decorate(c), 'Perubahan disimpan')
  }],
  ['GET', /^\/admin\/instructors$/, ({ q }) => {
    let list = db.instructors.map((i) => ({ ...i, provider: prvOf(i.provider_id), category: catOf(i.category_id), account: accountOf(i) }))
    if (q.search) list = list.filter((i) => i.name.toLowerCase().includes(q.search.toLowerCase()))
    if (q.verified) list = list.filter((i) => String(i.is_verified) === q.verified)
    return ok(paginate(list, { per_page: 10, ...q }))
  }],
  // Create/update pakar. Sengaja ADA di mock meski sisi Go yang menegakkan
  // aturannya: mock yang selalu berhasil membuat penjaga "email sudah dipakai
  // pakar lain" tidak pernah terlihat saat VITE_USE_MOCK=true — dan build statis
  // yang ter-deploy justru berjalan di atas mock.
  ['POST', /^\/admin\/instructors$/, ({ body }) => {
    const name = String(body.name || '').trim()
    if (!name) fail(400, 'Nama pakar wajib diisi.')
    const ins = {
      id: uidNew('ins'),
      provider_id: body.provider_id || db.providers[0]?.id,
      name,
      headline: body.headline || '',
      slug: name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
      bio: body.bio || '',
      avatar_url: body.avatar_url || '',
      category_id: body.category_id || '',
      credentials: [], social_links: {},
      is_verified: !!body.is_verified,
      is_active: body.is_active !== false,
      rating_avg: 0, review_count: 0, student_count: 0, course_count: 0,
      account_email: '',
    }
    linkAccountMock(ins, body.account_email)
    db.instructors.push(ins); persist()
    return ok({ ...ins, provider: prvOf(ins.provider_id), category: catOf(ins.category_id), account: accountOf(ins) }, 'Pakar ditambahkan')
  }],
  ['PUT', /^\/admin\/instructors\/([^/]+)$/, ({ m, body }) => {
    const ins = db.instructors.find((x) => x.id === m[1])
    if (!ins) fail(404, 'Pakar tidak ditemukan.')
    const name = String(body.name || '').trim()
    if (name && name !== ins.name) {
      ins.name = name
      ins.slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    }
    ins.headline = body.headline ?? ins.headline
    ins.bio = body.bio ?? ins.bio
    ins.avatar_url = body.avatar_url ?? ins.avatar_url
    if (body.category_id) ins.category_id = body.category_id
    // Sama seperti is_active: medan yang TIDAK dikirim berarti "jangan ubah".
    // Ditulis `!!body.is_verified`, setiap simpan yang lupa menyertakannya
    // mencabut verifikasi diam-diam — dan verifikasi itu sesuatu yang diperoleh.
    if (body.is_verified != null) ins.is_verified = !!body.is_verified
    if (body.is_active != null) ins.is_active = !!body.is_active
    // Putus diperiksa lebih dulu supaya "putuskan lalu sambungkan ke alamat
    // lain" bisa dilakukan dalam satu simpan.
    if (body.unlink_account) ins.account_email = ''
    linkAccountMock(ins, body.account_email)
    if (body.source_lang) ins.source_lang = body.source_lang
    // Digabung, bukan ditimpa: form hanya mengirim tab bahasa yang berubah,
    // jadi menimpa akan menghapus tiga bahasa lain tiap simpan.
    if (body.translations) {
      const tr = ins.translations || {}
      Object.entries(body.translations).forEach(([l, blk]) => {
        const cur = tr[l] || { fields: {}, origin: {} }
        Object.entries(blk?.fields || {}).forEach(([k, v]) => {
          if (!String(v || '').trim()) { delete cur.fields[k]; delete cur.origin[k]; return }
          cur.fields = { ...cur.fields, [k]: v }
          cur.origin = { ...cur.origin, [k]: 'manual' }
        })
        tr[l] = cur
      })
      ins.translations = tr
    }
    persist()
    return ok({ ...ins, provider: prvOf(ins.provider_id), category: catOf(ins.category_id), account: accountOf(ins) }, 'Pakar diperbarui')
  }],
  // Terjemah tiruan.
  //
  // Hasilnya SENGAJA diberi awalan "[EN] " dan seterusnya, bukan kalimat yang
  // terlihat sungguhan. Mock adalah alat desain: terjemahan palsu yang terbaca
  // meyakinkan akan membuat tab bahasa lolos tinjauan tanpa ada yang pernah
  // memanggil penerjemah sungguhan — dan yang ketahuan belakangan bukan
  // tampilannya, melainkan bahwa fiturnya tidak pernah tersambung.
  ['POST', /^\/admin\/instructors\/([^/]+)\/translate$/, ({ m, body }) => {
    const ins = db.instructors.find((x) => x.id === m[1])
    if (!ins) fail(404, 'Pakar tidak ditemukan.')
    const src = ins.source_lang || 'id'
    const tr = ins.translations || {}
    const now = new Date().toISOString()
    // `to` kosong berarti SEMUA bahasa — sama seperti sisi Go. Kalau mock
    // mengabaikan `to`, tombol yang seharusnya menerjemahkan satu tab akan
    // terlihat benar di sini dan cuma berbeda di server.
    const minta = Array.isArray(body?.to) && body.to.length ? body.to : ['id', 'en', 'ms', 'ar']
    ;minta.filter((l) => l !== src && ['id', 'en', 'ms', 'ar'].includes(l)).forEach((l) => {
      const blok = tr[l] || { fields: {}, origin: {} }
      ;['headline', 'bio'].forEach((k) => {
        // Yang sudah disunting manusia TIDAK ditimpa — penjaga yang sama
        // dengan sisi Go, dan justru yang paling perlu terlihat saat meninjau.
        if (blok.origin?.[k] === 'manual') return
        if (!ins[k]) return
        blok.fields = { ...blok.fields, [k]: `[${l.toUpperCase()}] ${ins[k]}` }
        blok.origin = { ...blok.origin, [k]: 'ai' }
      })
      blok.from = src; blok.at = now
      tr[l] = blok
    })
    ins.translations = tr; ins.source_lang = src; persist()
    return ok({ ...ins, account: accountOf(ins) }, 'Terjemahan selesai.')
  }],
  ['POST', /^\/admin\/instructors\/bulk$/, ({ body }) => {
    body.ids.forEach((id) => {
      const i = db.instructors.find((x) => x.id === id)
      if (!i) return
      if (body.action === 'verify') i.is_verified = true
      if (body.action === 'unverify') i.is_verified = false
      if (body.action === 'activate') i.is_active = true
      if (body.action === 'deactivate') i.is_active = false
      if (body.action === 'delete') db.instructors = db.instructors.filter((x) => x.id !== id)
    })
    persist(); return ok({ affected: body.ids.length }, 'Pakar diperbarui')
  }],
  ['GET', /^\/admin\/enrollments$/, ({ q }) => {
    let list = db.enrollments.map((e) => ({ ...e, course: db.courses.find((c) => c.id === e.course_id), user_name: seed.users[0].name, user_email: seed.users[0].email }))
    if (q.status) list = list.filter((e) => e.status === q.status)
    return ok(paginate(list, { per_page: 10, ...q }))
  }],
  ['GET', /^\/admin\/reviews$/, ({ q }) => {
    let list = [...db.reviews]
    if (q.status) list = list.filter((r) => r.status === q.status)
    if (q.stars) list = list.filter((r) => r.stars === +q.stars)
    return ok(paginate(list, { per_page: 10, ...q }))
  }],
  ['POST', /^\/admin\/reviews\/bulk$/, ({ body }) => {
    body.ids.forEach((id) => {
      const r = db.reviews.find((x) => x.id === id)
      if (!r) return
      if (body.action === 'hide') r.status = 'hidden'
      if (body.action === 'publish') r.status = 'published'
      if (body.action === 'delete') db.reviews = db.reviews.filter((x) => x.id !== id)
    })
    persist(); return ok({ affected: body.ids.length }, 'Ulasan diperbarui')
  }],
  ['GET', /^\/admin\/payouts$/, ({ q }) => ok(paginate(db.payouts, { per_page: 10, ...q }))],
  ['POST', /^\/admin\/payouts\/bulk$/, ({ body }) => {
    body.ids.forEach((id) => { const p = db.payouts.find((x) => x.id === id); if (p && body.action === 'mark_paid') { p.status = 'paid'; p.paid_at = new Date().toISOString() } })
    persist(); return ok({ affected: body.ids.length }, 'Payout diperbarui')
  }],
  ['GET', /^\/admin\/providers$/, ({ q }) => ok(paginate(db.providers, { per_page: 10, ...q }))],
  ['POST', /^\/admin\/providers\/bulk$/, ({ body }) => {
    body.ids.forEach((id) => { const p = db.providers.find((x) => x.id === id); if (!p) return; if (body.action === 'verify') p.is_verified = true; if (body.action === 'deactivate') p.is_active = false; if (body.action === 'activate') p.is_active = true })
    persist(); return ok({ affected: body.ids.length }, 'Provider diperbarui')
  }],
  ['GET', /^\/admin\/categories$/, ({ q }) => ok(paginate(db.categories, { per_page: 20, ...q }))],
  ['GET', /^\/admin\/price-tiers$/, ({ q }) => ok(paginate(db.priceTiers, { per_page: 20, ...q }))],
  ['GET', /^\/admin\/coupons$/, ({ q }) => ok(paginate(db.coupons, { per_page: 20, ...q }))],
  ['GET', /^\/admin\/live-classes$/, ({ q }) => ok(paginate(db.liveClasses.map((l) => ({ ...l, instructor: insOf(l.instructor_id), provider: prvOf(l.provider_id) })), { per_page: 10, ...q }))],
  ['GET', /^\/admin\/qa$/, ({ q }) => {
    let list = db.qaQuestions.map((x) => ({ ...x, instructor: insOf(x.instructor_id), category: catOf(x.category_id) }))
    if (q.status) list = list.filter((x) => x.status === q.status)
    return ok(paginate(list, { per_page: 10, ...q }))
  }],
  ['POST', /^\/admin\/qa\/([^/]+)\/answer$/, ({ m, body }) => {
    const qq = db.qaQuestions.find((x) => x.id === m[1])
    if (!qq) fail(404, 'Pertanyaan tidak ditemukan')
    qq.answers.push({ id: uidNew('qaa'), instructor_id: qq.instructor_id, body: body.body, created_at: new Date().toISOString() })
    qq.status = 'answered'; persist()
    return ok(qq, 'Jawaban terkirim')
  }],
  ['GET', /^\/admin\/consultations$/, ({ q }) => ok(paginate(db.bookings.map((b) => ({ ...b, instructor: insOf(b.instructor_id), slot: db.consultationSlots.find((s) => s.id === b.slot_id), user_name: seed.users[0].name })), { per_page: 10, ...q }))],
  ['GET', /^\/admin\/courses\/([^/]+)\/curriculum$/, ({ m }) => ok(curriculumOf(m[1], { unlocked: true }))],
  ['PUT', /^\/admin\/courses\/([^/]+)\/curriculum$/, ({ m, body }) => {
    // Menyimpan kurikulum = menghapus seluruh pohon lalu membangunnya ulang,
    // sama seperti di server. Karena itu kolom yang TIDAK datang dari formulir
    // wajib disalin dari baris lama, dan disalin di sini juga: kalau mock
    // membuang video tiap kali urutan pelajaran digeser, gejalanya justru
    // muncul di build demo yang dipakai meninjau desain.
    const prevLesson = Object.fromEntries(db.lessons.filter((x) => x.course_id === m[1]).map((x) => [x.id, x]))
    db.modules = db.modules.filter((x) => x.course_id !== m[1])
    db.lessons = db.lessons.filter((x) => x.course_id !== m[1])
    body.modules.forEach((mo, mi) => {
      const id = mo.id || uidNew('mod')
      db.modules.push({ id, course_id: m[1], title: mo.title, description: mo.description || '', position: mi + 1, drip_type: mo.drip_type || 'none', drip_days: mo.drip_days || null })
      ;(mo.lessons || []).forEach((l, li) => {
        const prev = prevLesson[l.id] || {}
        db.lessons.push({
          ...prev,
          id: l.id || uidNew('les'), module_id: id, course_id: m[1], title: l.title, type: l.type,
          position: li + 1, is_preview: !!l.is_preview,
          duration_sec: l.duration_sec || prev.duration_sec || null, is_published: true,
        })
      })
    })
    persist(); return ok(curriculumOf(m[1], { unlocked: true }), 'Kurikulum disimpan')
  }],
]

/* --------------------------------------------------------------- driver --- */
export async function mockRequest(method, path, { query = {}, body = null } = {}) {
  await sleep(latency())
  const errRate = +(env.VITE_MOCK_ERROR_RATE ?? 0)
  if (errRate && Math.random() < errRate) throw new ApiError(500, 'Mock: kegagalan jaringan simulasi')

  for (const [verb, re, handler] of routes) {
    if (verb !== method) continue
    const m = re.exec(path)
    if (m) return handler({ m, q: query, body: body || {} })
  }
  throw new ApiError(404, `Mock route belum ada: ${method} ${path}`)
}
